import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MessageCircle, Clock, MapPin, ShieldCheck, ChevronRight } from "lucide-react";
import { SEO } from "../components/SEO";
import odooApi, { buildPhoneHref, buildWhatsAppUrl, getDisplayPhoneNumber } from "../services/odooApi";
import { useLanguage } from "../contexts/LanguageContext";

const FALLBACK_TITLE = "Support";
const FALLBACK_SUBTITLE = "Get help with bookings, your account, technical issues, and active rentals.";

export const SupportPage = () => {
  const { t } = useLanguage();
  const [pageContent, setPageContent] = useState(null);
  const [config, setConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const loadPage = async () => {
      try {
        const [page, cmsConfig] = await Promise.all([
          odooApi.getPageContent("support"),
          odooApi.getCmsConfig(),
        ]);

        if (!active) {
          return;
        }

        if (page) {
          setPageContent(page);
        }
        if (cmsConfig) {
          setConfig(cmsConfig);
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    loadPage();

    return () => {
      active = false;
    };
  }, []);

  const title = pageContent?.title || FALLBACK_TITLE;
  const subtitle = pageContent?.subtitle || FALLBACK_SUBTITLE;
  const heroImage = pageContent?.hero_image_url || "";
  const phoneLabel = getDisplayPhoneNumber(config?.phone);
  const phoneHref = buildPhoneHref(config?.phone);
  const whatsappLabel = getDisplayPhoneNumber(config?.whatsapp || config?.phone);
  const whatsappHref = buildWhatsAppUrl(
    config?.whatsapp || config?.phone,
    "Hi! I need help with the Velaris app or a booking.",
  );
  const email = config?.email || "info@velaris.ae";
  const workingHours = config?.working_hours || "24/7 Available";
  const address = config?.address || "Business Bay, Dubai, United Arab Emirates";

  const contactCards = [
    {
      title: t("contact.phone", "Phone"),
      description: t("contact.callSupport", "Speak directly with our rental advisors"),
      value: phoneLabel,
      href: phoneHref,
      icon: Phone,
      onClick: () => odooApi.trackClick({ click_type: "call" }),
    },
    {
      title: t("contact.whatsapp", "WhatsApp"),
      description: t("contact.instantResponse", "Get instant response via WhatsApp"),
      value: whatsappLabel,
      href: whatsappHref,
      icon: MessageCircle,
      onClick: () => odooApi.trackClick({ click_type: "whatsapp" }),
    },
    {
      title: t("contact.email", "Email"),
      description: t("contact.emailReply", "Best for detailed requests and partnership inquiries"),
      value: email,
      href: `mailto:${email}`,
      icon: Mail,
    },
  ].filter((card) => card.href !== '#');

  const infoCards = [
    {
      title: t("contact.hours", "Working Hours"),
      value: workingHours,
      icon: Clock,
    },
    {
      title: t("contact.address", "Address"),
      value: address,
      icon: MapPin,
    },
  ];

  return (
    <>
      <SEO
        title={title}
        description={subtitle}
        keywords="velaris support, app support, booking support, customer support, contact velaris"
        breadcrumbs={[
          { name: t("nav.home", "Home"), url: "/" },
          { name: t("common.support", FALLBACK_TITLE) },
        ]}
      />

      <div className="min-h-screen bg-white" data-testid="support-page">
        <div className="relative overflow-hidden bg-black py-20 sm:py-24">
          {heroImage ? (
            <>
              <img
                src={heroImage}
                alt={title}
                className="absolute inset-0 h-full w-full object-cover opacity-30"
              />
              <div className="absolute inset-0 bg-black/75" />
            </>
          ) : (
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(212,175,55,0.28),_transparent_45%),radial-gradient(circle_at_bottom_right,_rgba(37,211,102,0.16),_transparent_30%),linear-gradient(135deg,_#000_0%,_#111827_100%)]" />
          )}

          <div className="relative z-10 mx-auto flex max-w-5xl flex-col items-start px-4 sm:px-6 lg:px-8">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-[#D4AF37]">
              <ShieldCheck className="h-4 w-4" />
              {t("common.support", FALLBACK_TITLE)}
            </div>
            <h1 className="font-[var(--font-display)] text-4xl font-bold text-white sm:text-5xl">
              {title}
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-7 text-white/70 sm:text-lg">
              {subtitle}
            </p>
          </div>
        </div>

        <section className="py-16 sm:py-20">
          <div className="mx-auto grid max-w-6xl gap-8 px-4 sm:px-6 lg:grid-cols-[1.15fr_0.85fr] lg:px-8">
            <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8 lg:p-10">
              {isLoading ? (
                <div className="space-y-4">
                  <div className="h-5 w-40 animate-pulse rounded bg-gray-200" />
                  <div className="h-5 w-full animate-pulse rounded bg-gray-100" />
                  <div className="h-5 w-5/6 animate-pulse rounded bg-gray-100" />
                  <div className="h-5 w-4/6 animate-pulse rounded bg-gray-100" />
                </div>
              ) : pageContent?.content ? (
                <div
                  className="text-gray-700 leading-8
                    [&_a]:font-medium [&_a]:text-[#B8860B] [&_a]:underline-offset-4 hover:[&_a]:text-[#8f6b00] hover:[&_a]:underline
                    [&_h1]:mb-6 [&_h1]:mt-10 [&_h1]:font-[var(--font-display)] [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:text-gray-950
                    [&_h2]:mb-5 [&_h2]:mt-10 [&_h2]:font-[var(--font-display)] [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-gray-950
                    [&_h3]:mb-4 [&_h3]:mt-8 [&_h3]:font-[var(--font-display)] [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-gray-900
                    [&_li]:mb-3
                    [&_ol]:my-5 [&_ol]:list-decimal [&_ol]:space-y-3 [&_ol]:pl-6
                    [&_p]:mb-5
                    [&_strong]:font-semibold [&_strong]:text-gray-950
                    [&_ul]:my-5 [&_ul]:list-disc [&_ul]:space-y-3 [&_ul]:pl-6"
                  dangerouslySetInnerHTML={{ __html: pageContent.content }}
                />
              ) : (
                <div className="rounded-2xl border border-dashed border-[#D4AF37]/50 bg-[#D4AF37]/5 p-6 text-gray-700">
                  <h2 className="font-[var(--font-display)] text-2xl font-semibold text-gray-950">
                    Support page content not published yet
                  </h2>
                  <p className="mt-3 leading-7">
                    Add or update the support page from Odoo Dashboard &gt; Website CMS &gt; Page Content, then open this page again.
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-6">
              <div className="rounded-3xl border border-gray-200 bg-gray-50 p-6 sm:p-8">
                <p className="text-sm uppercase tracking-[0.22em] text-[#D4AF37]">
                  {t("common.support", FALLBACK_TITLE)}
                </p>
                <h2 className="mt-3 font-[var(--font-display)] text-2xl font-semibold text-gray-950">
                  Need immediate help?
                </h2>
                <p className="mt-3 text-sm leading-7 text-gray-600">
                  Use the live contact details below. Phone, WhatsApp, email, address, and working hours are all managed from Odoo.
                </p>
              </div>

              <div className="grid gap-4">
                {contactCards.map((card) => {
                  const Icon = card.icon;
                  return (
                    <a
                      key={card.title}
                      href={card.href}
                      target={card.href.startsWith("http") ? "_blank" : undefined}
                      rel={card.href.startsWith("http") ? "noopener noreferrer" : undefined}
                      onClick={card.onClick}
                      className="group rounded-3xl border border-gray-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-1 hover:border-[#D4AF37]/40 hover:shadow-md"
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-black text-white">
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-sm font-semibold text-gray-950">{card.title}</p>
                              <p className="mt-1 text-sm leading-6 text-gray-600">{card.description}</p>
                            </div>
                            <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-gray-400 transition-colors group-hover:text-[#D4AF37]" />
                          </div>
                          <p className="mt-4 break-words text-sm font-medium text-[#B8860B]">{card.value}</p>
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                {infoCards.map((card) => {
                  const Icon = card.icon;
                  return (
                    <div
                      key={card.title}
                      className="rounded-3xl border border-gray-200 bg-white p-5 shadow-sm"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#D4AF37] text-black">
                          <Icon className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="text-xs uppercase tracking-[0.18em] text-gray-500">{card.title}</p>
                          <p className="mt-2 whitespace-pre-line text-sm leading-6 text-gray-700">{card.value}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="rounded-3xl bg-black px-6 py-8 text-white sm:px-8">
                <p className="text-sm uppercase tracking-[0.22em] text-[#D4AF37]">
                  Need a written follow-up?
                </p>
                <h2 className="mt-2 font-[var(--font-display)] text-2xl font-semibold">
                  Use the full contact form
                </h2>
                <p className="mt-3 text-sm leading-7 text-white/70">
                  For longer requests, partnership questions, or detailed booking context, use the website contact page.
                </p>
                <Link
                  to="/contact"
                  className="mt-5 inline-flex items-center justify-center rounded-sm bg-[#D4AF37] px-6 py-3 font-semibold text-black transition-colors hover:bg-[#c7a022]"
                >
                  Contact Us
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default SupportPage;
